/**
 * POStockConfigStep — Step 2 of POTransactionsStepper
 * Qty and Unit Cost only. No lot/batch/expiry — those are captured at Confirm Delivery.
 * Vendor, PO#, PO Date are read-only (pre-filled). Invoice is optional.
 */
import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Grid, Chip, InputAdornment, alpha, useTheme,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemButton, ListItemText,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton
} from '@mui/material';
import { Settings as SettingsIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../supabaseClient';

const TYPE_COLORS = {
  products: '#1565c0', chemicals: '#7b1fa2', caps: '#00796b',
  bottles: '#2e7d32', labels: '#e65100', supplies: '#5d4037',
};
const TYPE_LABELS = {
  products: 'Product', chemicals: 'Chemical', caps: 'Cap',
  bottles: 'Bottle', labels: 'Label', supplies: 'Supply',
};

export default function POStockConfigStep({ companyId, selectedItems, itemsConfig, onItemsConfigChange, shared, onSharedChange }) {
  const theme = useTheme();
  const [unitConfigs,        setUnitConfigs]        = useState([]);
  const [configDialogOpen,   setConfigDialogOpen]   = useState(false);
  const [configDialogTarget, setConfigDialogTarget] = useState(null); // { key, lotIdx }
  const [configSearch,       setConfigSearch]       = useState('');
  const [variantOptions,     setVariantOptions]     = useState({}); // { [key]: [{id, name}] }
  const [variantDialogKey,   setVariantDialogKey]   = useState(null); // item key when dialog open
  const [variantSearch,      setVariantSearch]      = useState('');

  const hasProducts = selectedItems.some(i => i.inventoryType === 'products');

  useEffect(() => {
    if (!hasProducts || !companyId) return;
    supabase.from('unit_configurations').select('*')
      .eq('company_id', companyId).eq('is_active', true).order('name')
      .then(({ data }) => setUnitConfigs(data || []));
  }, [hasProducts, companyId]);

  useEffect(() => {
    const productItems = selectedItems.filter(i => i.inventoryType === 'products');
    if (!productItems.length) return;
    const productIds = productItems.map(i => i.id);
    supabase.from('product_variants').select('id, name, product_id')
      .in('product_id', productIds).eq('is_active', true)
      .then(({ data }) => {
        const opts = {};
        productItems.forEach(item => {
          const key = `products:${item.id}`;
          opts[key] = (data || []).filter(v => v.product_id === item.id);
        });
        setVariantOptions(opts);
      });
  }, [selectedItems, companyId]);

  const tfSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.9)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.25) },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };

  const setLotField = (itemKey, lotIdx, field, value) => {
    const prev = itemsConfig[itemKey] || { lots: [] };
    const lots = [...(prev.lots || [])];
    lots[lotIdx] = { ...lots[lotIdx], [field]: value };
    onItemsConfigChange({ ...itemsConfig, [itemKey]: { ...prev, lots } });
  };

  const setLotMultiField = (itemKey, lotIdx, fields) => {
    const prev = itemsConfig[itemKey] || { lots: [] };
    const lots = [...(prev.lots || [])];
    lots[lotIdx] = { ...lots[lotIdx], ...fields };
    onItemsConfigChange({ ...itemsConfig, [itemKey]: { ...prev, lots } });
  };

  const setSharedField = (field, value) => onSharedChange({ ...shared, [field]: value });

  const setItemVariant = (key, variantId) => {
    const prev = itemsConfig[key] || { lots: [] };
    onItemsConfigChange({ ...itemsConfig, [key]: { ...prev, variantId: variantId || null } });
  };

  return (
    <Box>
      {/* Shared vendor / invoice — vendor, PO#, PO date read-only; invoice optional */}
      <Paper elevation={0} sx={{
        p: 2.5, mb: 3, borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
        bgcolor: alpha(theme.palette.grey[50], 0.8)
      }}>
        <Typography variant="caption" sx={{
          mb: 2, display: 'block', fontWeight: 500, color: 'text.secondary',
          fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px'
        }}>
          Vendor &amp; Invoice — Optional, applies to all items
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Vendor"
              value={shared.vendor?.name || ''}
              InputProps={{ readOnly: true }}
              sx={tfSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Invoice #"
              value={shared.invoiceNumber || ''}
              onChange={e => setSharedField('invoiceNumber', e.target.value)}
              placeholder="Optional"
              sx={tfSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Invoice Date (optional)"
                value={shared.invoiceDate ? dayjs(shared.invoiceDate) : null}
                onChange={v => setSharedField('invoiceDate', v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: tfSx } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="PO #"
              value={shared.poNumber || ''}
              InputProps={{ readOnly: true }}
              sx={tfSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="PO Date"
                value={shared.poDate ? dayjs(shared.poDate) : null}
                readOnly
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: tfSx } }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      {/* Per-item cards */}
      {selectedItems.map(item => {
        const key        = `${item.inventoryType}:${item.id}`;
        const cfg        = itemsConfig[key] || { lots: [] };
        const color      = TYPE_COLORS[item.inventoryType] || '#666';
        const lots       = cfg.lots || [];
        const isProduct  = item.inventoryType === 'products';
        const isChemical = item.inventoryType === 'chemicals';

        return (
          <Paper key={key} elevation={0} sx={{
            mb: 2, borderRadius: 2, overflow: 'hidden',
            border: `1px solid ${alpha(color, 0.25)}`
          }}>
            {/* Item header */}
            <Box sx={{
              px: 2.5, py: 1.5,
              bgcolor: alpha(color, 0.07),
              borderBottom: `1px solid ${alpha(color, 0.2)}`,
              display: 'flex', alignItems: 'center', gap: 1
            }}>
              <Chip
                label={TYPE_LABELS[item.inventoryType] || item.inventoryType}
                size="small"
                sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 500, height: 20, fontSize: '0.7rem' }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 500, color }}>
                {item.name}
              </Typography>
              {item.sku && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  SKU: {item.sku}
                </Typography>
              )}
            </Box>

            {/* Lot entry */}
            <Box sx={{ p: 2.5 }}>
              {/* Variant selector — product only, item-level (not per lot) */}
              {isProduct && (() => {
                const selectedVariant = (variantOptions[key] || []).find(v => v.id === cfg.variantId);
                return (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined" size="small"
                      onClick={() => { setVariantDialogKey(key); setVariantSearch(''); }}
                      sx={{
                        borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                        border: `1px solid ${cfg.variantId ? alpha(color, 0.5) : alpha(theme.palette.error.main, 0.5)}`,
                        color: cfg.variantId ? color : 'error.main',
                      }}
                    >
                      {selectedVariant ? selectedVariant.name : 'Select Variant *'}
                    </Button>
                    {!cfg.variantId && (
                      <Typography variant="caption" color="error" sx={{ ml: 1 }}>Required</Typography>
                    )}
                  </Box>
                );
              })()}

              {lots.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No line items configured.</Typography>
              ) : lots.map((lot, li) => {
                // Chemical unit info
                const purchaseUnit = item.primary_unit_type || '';
                const primaryUnit  = item.primary_unit_measure || '';
                const convFactor   = item.primary_unit_quantity ? parseFloat(item.primary_unit_quantity) : null;
                const qty          = parseFloat(lot.quantity) || 0;
                const totalBase    = convFactor ? qty * convFactor : null;
                const qtyLabel     = isChemical && purchaseUnit ? `Quantity (${purchaseUnit})` : 'Quantity';
                const helperText   = isChemical && purchaseUnit && convFactor
                  ? `1 ${purchaseUnit} = ${convFactor} ${primaryUnit}${totalBase ? ` · Total: ${totalBase.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${primaryUnit}` : ''}`
                  : undefined;

                return (
                  <Box key={li}>
                    {isProduct ? (
                      <>
                        {/* Unit Config selector */}
                        <Box sx={{ mb: 1.5 }}>
                          <Button
                            variant="outlined" size="small"
                            startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                            onClick={() => { setConfigDialogTarget({ key, lotIdx: li }); setConfigSearch(''); setConfigDialogOpen(true); }}
                            sx={{
                              borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                              border: `1px solid ${lot._configId ? alpha(color, 0.5) : alpha(theme.palette.error.main, 0.5)}`,
                              color: lot._configId ? color : 'error.main',
                            }}
                          >
                            {lot._configName || 'Select Unit Configuration *'}
                          </Button>
                          {!lot._configId && <Typography variant="caption" color="error" sx={{ ml: 1 }}>Required</Typography>}
                        </Box>

                        {/* Qty fields shown after config selected */}
                        {lot._configId && (
                          <Grid container spacing={2} sx={{ mb: 1.5 }}>
                            <Grid item xs={12} sm={lot._enablePallets ? 4 : (lot._enableCases ? 4 : 6)}>
                              <TextField
                                fullWidth size="small" label="Units" type="number"
                                value={lot.unitQty || ''}
                                onChange={e => setLotField(key, li, 'unitQty', e.target.value)}
                                inputProps={{ min: 0 }} sx={tfSx}
                              />
                            </Grid>
                            {lot._enableCases && (() => {
                              const cUnits = (parseFloat(lot.caseQty) || 0) * (lot._unitsPerCase || 1);
                              return (
                              <Grid item xs={12} sm={lot._enablePallets ? 4 : 4}>
                                <TextField
                                  fullWidth size="small" label="Cases" type="number"
                                  value={lot.caseQty || ''}
                                  onChange={e => setLotField(key, li, 'caseQty', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  helperText={`${lot._unitsPerCase || 1} units/case${cUnits ? ` = ${cUnits.toLocaleString()} units` : ''}`}
                                  sx={tfSx}
                                />
                              </Grid>
                              );
                            })()}
                            {lot._enablePallets && (() => {
                              const pUnits = (parseFloat(lot.palletQty) || 0) * (lot._casesPerPallet || 1) * (lot._unitsPerCase || 1);
                              return (
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  fullWidth size="small" label="Pallets" type="number"
                                  value={lot.palletQty || ''}
                                  onChange={e => setLotField(key, li, 'palletQty', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  helperText={`${lot._casesPerPallet || 1} cases/pallet${pUnits ? ` = ${pUnits.toLocaleString()} units` : ''}`}
                                  sx={tfSx}
                                />
                              </Grid>
                              );
                            })()}
                          </Grid>
                        )}

                        {/* Unit Cost */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small" label="Unit Cost" type="number"
                              value={lot.unitCost || ''}
                              onChange={e => setLotField(key, li, 'unitCost', e.target.value)}
                              inputProps={{ min: 0, step: '0.01' }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              sx={tfSx}
                            />
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <Grid container spacing={2} sx={{ mb: li < lots.length - 1 ? 1 : 0 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small" label={qtyLabel}
                            type="number"
                            value={lot.quantity || ''}
                            onChange={e => setLotField(key, li, 'quantity', e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText={helperText}
                            sx={tfSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small" label="Unit Cost"
                            type="number"
                            value={lot.unitCost || ''}
                            onChange={e => setLotField(key, li, 'unitCost', e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            sx={tfSx}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}

      {/* Unit Configuration Selection Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 500 }}>Select Unit Configuration</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" placeholder="Search configurations..."
            value={configSearch} onChange={e => setConfigSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List disablePadding>
            {unitConfigs
              .filter(c => c.name.toLowerCase().includes(configSearch.toLowerCase()))
              .map(cfg => (
                <ListItem key={cfg.id} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (configDialogTarget) {
                        const { key, lotIdx } = configDialogTarget;
                        setLotMultiField(key, lotIdx, {
                          _configId:       cfg.id,
                          _configName:     cfg.name,
                          _unitsPerCase:   cfg.units_per_case   || 1,
                          _casesPerPallet: cfg.cases_per_pallet || 1,
                          _enableCases:    cfg.enable_cases  ?? true,
                          _enablePallets:  cfg.enable_pallets ?? false,
                        });
                      }
                      setConfigDialogOpen(false);
                    }}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={cfg.name}
                      secondary={`${cfg.units_per_case || 1} units/case${cfg.enable_pallets ? ` · ${cfg.cases_per_pallet || 1} cases/pallet` : ''}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            {unitConfigs.filter(c => c.name.toLowerCase().includes(configSearch.toLowerCase())).length === 0 && (
              <ListItem><ListItemText primary="No configurations found" /></ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Variant Selection Dialog */}
      {(() => {
        const variants = variantDialogKey ? (variantOptions[variantDialogKey] || []) : [];
        const filtered = variants.filter(v => v.name?.toLowerCase().includes(variantSearch.toLowerCase()));
        return (
          <Dialog open={!!variantDialogKey} onClose={() => setVariantDialogKey(null)} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, minHeight: '50vh' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Select Variant</Typography>
              <IconButton onClick={() => setVariantDialogKey(null)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                <TextField fullWidth placeholder="Search variants..."
                  value={variantSearch} onChange={e => setVariantSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Variant</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2, width: 120 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(v => {
                    const cfg = variantDialogKey ? (itemsConfig[variantDialogKey] || {}) : {};
                    const isSelected = cfg.variantId === v.id;
                    return (
                      <TableRow key={v.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}>
                        <TableCell sx={{ py: 2, pl: 3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button size="small" variant="outlined"
                            onClick={() => { setItemVariant(variantDialogKey, v.id); setVariantDialogKey(null); }}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 2,
                              bgcolor: isSelected ? 'black' : 'transparent', color: isSelected ? 'white' : 'inherit',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No variants found</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        );
      })()}
    </Box>
  );
}
